'use client';

import { Group, Stack, Text } from '@mantine/core';
import { instantMeiliSearch } from '@meilisearch/instant-meilisearch';
import { useEffect, useState } from 'react';
import React from 'react';
import { Configure, InstantSearch } from 'react-instantsearch';

import 'instantsearch.css/themes/satellite.css';

import {
  CustomAutocompleteRefinement,
  InfluenceTargetPaper,
  SearchAutocomplete,
} from '@/components';
import { useIssuePage } from '@/contexts';

import styles from './InfluenceTargetSelector.module.css';
// This is the type of the data stored in the state
export type InfluenceTargetType = {
  civicEngineId: string;
  title: string;
  type: string;
  subtitle?: string;
  geoId: string;
  officeName?: string;
  measureTitle?: string;
  measureSummary?: string;
};

// This is the type of the data returned by the search client
type InfluenceTargetResult = {
  id: string;
  originalId: string;
  type: string;
  name: string;
  geoId: string;
  state?: string;
  bioText?: string;
  fullName?: string;
  title?: string;
  summary?: string;
};

type InfluenceTargetFromDB = {
  id: string;
  title: string;
  subtitle: string;
};

export function InfluenceTargetSelector({
  influenceTargets,
  onAddInfluenceTarget,
  onRemoveInfluenceTarget,
  oldInfluenceTargets = [],
  onRemoveOldInfluenceTarget,
}: {
  influenceTargets: InfluenceTargetType[];
  onAddInfluenceTarget: (target: InfluenceTargetType) => void;
  onRemoveInfluenceTarget: (index: number) => void;
  oldInfluenceTargets?: InfluenceTargetFromDB[];
  onRemoveOldInfluenceTarget?: (index: number) => void;
}) {
  const { getIsFullEditMode, getIsStaticMode } = useIssuePage();

  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [selectedGeoId, setSelectedGeoId] = useState<string | null>(null);

  const handleAddInfluenceTarget = (influenceTarget: InfluenceTargetResult) => {
    let newTarget: InfluenceTargetType;
    // const unescapedId = unescapeBase64Url(influenceTarget.id);

    switch (influenceTarget.type) {
      case 'offices':
      case 'measures':
        newTarget = {
          civicEngineId: influenceTarget.originalId,
          title: influenceTarget.name,
          subtitle: influenceTarget.state,
          geoId: influenceTarget.geoId,
          type: influenceTarget.type,
          officeName: influenceTarget.name,
          measureTitle: influenceTarget.title,
          measureSummary: influenceTarget.summary,
        };
        break;
      // TODO currently not supporting people search
      default:
        return; // Exit if the type is not recognized
    }

    onAddInfluenceTarget(newTarget);
  };

  const handleRemoveInfluenceTarget = (index: number) => {
    onRemoveInfluenceTarget(index);
  };

  const handleRemoveOldInfluenceTarget = (index: number) => {
    onRemoveOldInfluenceTarget?.(index);
  };

  if (
    !process.env.NEXT_PUBLIC_MEILISEARCH_URL ||
    !process.env.NEXT_PUBLIC_MEILISEARCH_API_KEY
  ) {
    throw new Error('MeiliSearch environment variables are not set');
  }

  const { searchClient } = instantMeiliSearch(
    process.env.NEXT_PUBLIC_MEILISEARCH_URL,
    process.env.NEXT_PUBLIC_MEILISEARCH_API_KEY,
  );

  useEffect(() => {
    setSelectedGeoId(null);
  }, [selectedState]);

  return (
    <div className="container">
      <Stack
        align="center"
        gap="md"
      >
        {getIsFullEditMode() && (
          <>
            <div className="topLine">
              <InstantSearch
                indexName="places"
                // eslint-disable-next-line
                searchClient={searchClient as any}
              >
                <Group gap="md">
                  <CustomAutocompleteRefinement
                    attribute="state"
                    placeholder="State"
                    onSelect={(stateValue) => setSelectedState(stateValue)}
                    isDisabled={!getIsFullEditMode()}
                  />
                  <SearchAutocomplete
                    type="small"
                    placeholder="Search places"
                    handleItemSubmit={(item) => setSelectedGeoId(item.geoId)}
                    isDisabled={!getIsFullEditMode()}
                  />
                </Group>
              </InstantSearch>
            </div>

            <div className="bottomLine">
              <InstantSearch
                indexName="influence_targets"
                // eslint-disable-next-line
                searchClient={searchClient as any}
              >
                {selectedGeoId && (
                  <Configure filters={`geoId = "${selectedGeoId}"`} />
                )}
                {selectedState && !selectedGeoId && (
                  <Configure filters={`state = "${selectedState}"`} />
                )}
                <SearchAutocomplete
                  type="large"
                  placeholder="Search offices or measures"
                  handleItemSubmit={(item) => handleAddInfluenceTarget(item)}
                  isDisabled={!getIsFullEditMode()}
                />
              </InstantSearch>
            </div>
          </>
        )}

        {influenceTargets?.length == 0 && getIsStaticMode() && (
          <Text
            size="md"
            className={styles.noInfluenceTargets}
          >
            No offices selected.
          </Text>
        )}

        {influenceTargets.map((influenceTarget, index) => (
          <InfluenceTargetPaper
            key={`new-${index}`}
            title={influenceTarget.title}
            subtitle={influenceTarget.subtitle || ''}
            onRemove={() => handleRemoveInfluenceTarget(index)}
            index={index}
          />
        ))}

        {oldInfluenceTargets.map((influenceTarget, index) => (
          <InfluenceTargetPaper
            key={`old-${index}`}
            title={influenceTarget.title}
            subtitle={influenceTarget.subtitle}
            onRemove={() => handleRemoveOldInfluenceTarget(index)}
            index={index}
            isOld={true}
          />
        ))}
      </Stack>
    </div>
  );
}
